import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { Icon, TooltipComponent } from "../../../components/Component";
import { Button, Card, DropdownToggle, UncontrolledDropdown } from "reactstrap";
import { Drawer, Popconfirm, Table } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import SearchInput from "../../form-components/SearchComponent";
import { useSelector } from "react-redux";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../utils/skeleton-templates/table";
import RateCardCreation, {
  RatecardSubmit,
  WagesType,
} from "./RateCardCreation";
import { ratecardApis } from "../../../api/Ratecard";
import { formatCurrency } from "../../../utils/Utils";
import TopMenu from "../../../route/topMenu";
import { updateMenuData } from "../../../store/slices/misc/menuData";
import { useDispatch } from "react-redux";
import Head from "../../../layout/head/Head";
import Content from "../../../layout/content/Content";
import { AddAccess } from "../../../components/accessControl/AccessControl";

function RateCardList() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { data: SideNavCollapse } = useSelector(
    (state: any) => state.SideNavCollapseStatus
  );
  const [viewLaborRatesModal, setViewLaborRatesModal] = useState<{
    status: boolean;
    title: string;
    rates: WagesType[];
  }>({
    status: false,
    title: "",
    rates: [],
  });

  const columns: any = [
    {
      title: "Rate Card Title",
      dataIndex: "rateCardTitle",
      key: "rateCardTitle",
      width: 280,
      // fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`rateCard-${record?.rateCardId}`}
              onClick={() => {
                // fetchDataById(record?.rateCardId);
                setModalOpen({
                  status: true,
                  id: record?.rateCardId,
                  defaultData: {
                    rateCardTitle: "",
                    validityStartDate: "",
                    validityEndDate: "",
                    wages: [],
                  },
                });
              }}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`rateCard-${record?.rateCardId}`}
              direction="right"
              text={"Click To View " + record?.rateCardTitle + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },

    {
      title: "Validity Start Date",
      dataIndex: "validityStartDate",
      key: "validityStartDate",
      width: 150,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Validity End Date",
      dataIndex: "validityEndDate",
      key: "validityEndDate",
      width: 150,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    {
      title: "Rates",
      dataIndex: "viewRates",
      key: "viewRates",
      render: (text: any, record: any) => {
        const wagesLength = record?.rateCardWages?.length;
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`viewRates-${record?.rateCardId}`}
              onClick={() => {
                setViewLaborRatesModal({
                  status: true,
                  title: record?.rateCardTitle,
                  rates: record?.rateCardWages,
                });
              }}
            >
              View Rates &nbsp;
              {`( ${wagesLength} )`}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`viewRates-${record?.rateCardId}`}
              direction="right"
              text={"Click To View Rate Card Wages Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Created By",
      dataIndex: "createdByUserName",
      key: "createdByUserName",
      width: 150,
      render: (text: any) => <span>{text || "N/A"}</span>,
    },
    {
      title: "Created On",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
    },
    // {
    //   title: "Updated By",
    //   dataIndex: "updatedUserName",
    //   key: "updatedUserName",
    //   width: 150,
    // },
    {
      title: "Updated On",
      dataIndex: "updatedAt",
      key: "updatedAt",
      width: 150,
      render: (text: any) =>
        text ? dayjs(text)?.format("DD-MMM-YYYY") : "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (text: any) => {
        return (
          <div className="justify-start align-center">
            <h6
              className={`tb-status ms-1`}
              style={{
                color: `${text == "Active" ? "green" : "red"}`,
                fontSize: "14px",
              }}
            >
              {text == "Active" ? (
                <CheckCircleOutlined />
              ) : (
                <CloseCircleOutlined />
              )}{" "}
              {text}
            </h6>
          </div>
        );
      },
    },
  ];
  const methods = useForm({
    mode: "onChange",
  });
  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [searchQuery, setsearchQuery] = useState(null);

  const [modalOpen, setModalOpen] = useState<{
    status: boolean;
    id: any;
    defaultData: RatecardSubmit;
  }>({
    status: false,
    id: undefined,
    defaultData: {
      rateCardTitle: "",
      validityStartDate: "",
      validityEndDate: "",
      wages: [],
    },
  });

  const fetchData = async (params: any, append = false) => {
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
      } = params;
      const apiResponse = await ratecardApis.getRatecardList({
        page: current,
        pageSize: pageSize,
        searchQuery,
        direction,
        sortOrder,
        pageDiff,
        paginationId,
      });
      const { data, status } = apiResponse;
      if (status) {
        const { list, count } = data;
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));

        setDataSource((prev: any) => (append ? [...prev, ...list] : list));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId // Last item ID for backward direction
            : dataSource[0]?.paginationId; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
    });
  };

  const refreshListView = () => {
    try {
      setPagination({
        current: 1,
        pageSize: 10,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "40", "50"],
        total: 0,
      });
      refreshData({
        pageDiff: 0,
      }); // calling for initial data load , if the view type is table
    } catch (error) {}
  };

  useEffect(() => {
    dispatch(updateMenuData(TopMenu?.activeProjects));
    refreshListView();
  }, []);

  const refreshData = (overRideData?: any) => {
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      ...overRideData,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  useEffect(() => {
    const navState = location.state as any;
    if (navState?.fromNav) {
      setModalOpen({
        status: false,
        id: undefined,
        defaultData: {
          rateCardTitle: "",
          validityStartDate: "",
          validityEndDate: "",
          wages: [],
        },
      });
    }
  }, [location.key]);

  return modalOpen?.status ? (
    <>
      <Head title={"Rate Cards | Create | Edit"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <RateCardCreation
                id={modalOpen?.id}
                close={() => {
                  refreshData();
                  setModalOpen({
                    status: false,
                    id: undefined,
                    defaultData: {
                      rateCardTitle: "",
                      validityStartDate: "",
                      validityEndDate: "",
                      wages: [],
                    },
                  });
                }}
                refresh={() => {
                  refreshListView();
                }}
                extra={{
                  defaultValues: modalOpen?.defaultData,
                }}
              />
            </div>
          </div>
        </Card>
      </Content>
    </>
  ) : (
    <>
      <Head title={"Rate Cards"} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div
              className="card-inner card-inner-lg"
              style={{ minHeight: "87vh" }}
            >
              <FormProvider {...methods}>
                <form>
                  <div
                    className="nk-ibx-head p-0 m-0 mt-1 w-100 d-flex justify-content-between align-items-center"
                    style={{ minHeight: "40px", border: "0px" }}
                  >
                    <div className="nk-ibx-head-actions w-45 ms-1">
                      <ul className="nk-ibx-head-tools g-1 m-0 p-0 w-100 ">
                        <div className={`w-100 m-0 `}>
                          <SearchInput
                            name="search"
                            placeholder="Search Rate Card Title"
                            width="100%"
                            prefixIcon={<SearchOutlined />}
                            onDebouncedSearch={async (value: any) => {
                              setsearchQuery(value);
                              setPagination({
                                ...pagination,
                                current: 1,
                              });
                              await fetchData({
                                oldPage: 1,
                                current: 1,
                                sortOrder: "DESC",
                                pageSize: pagination.pageSize.toString(),
                                direction: "P",
                                pageDiff: 0,
                                paginationId: null,
                                searchQuery: value,
                              });
                            }}
                            debounceDelay={300} // Adjust delay as needed
                          />
                        </div>
                      </ul>
                    </div>
                    <ul className="nk-ibx-head-tools g-1 m-0 p-0 ">
                      <li>
                        <UncontrolledDropdown>
                          <DropdownToggle
                            tag="a"
                            className="dropdown-toggle btn btn-icon btn-trigger"
                            onClick={(ev) => {
                              refreshListView();
                            }}
                          >
                            <Icon name="redo"></Icon>
                          </DropdownToggle>
                        </UncontrolledDropdown>
                      </li>
                      <li className="pt-0 pb-0">
                        <AddAccess moduleName="Ratecards">
                          <Button
                            className="toggle"
                            color="primary"
                            size="sm"
                            onClick={() => {
                              setModalOpen({
                                status: true,
                                id: null,
                                defaultData: {
                                  rateCardTitle: "",
                                  validityStartDate: "",
                                  validityEndDate: "",
                                  wages: [],
                                },
                              });
                            }}
                          >
                            <Icon name={"plus"} />
                            <span>Add Rate Card</span>
                          </Button>
                        </AddAccess>
                      </li>
                    </ul>
                  </div>
                </form>
              </FormProvider>

              <div className="card-inner card-inner-lg p-0">
                <div className="content clearfix">
                  <div
                    style={{
                      width: screenSize - (SideNavCollapse ? 120 : 320) + "px",
                      margin: "10px auto",
                    }}
                  >
                    <SkeletonTable
                      loading={loading}
                      columns={columns as SkeletonTableColumnsType[]}
                    >
                      <Table
                        rowKey="uid"
                        columns={columns}
                        dataSource={dataSource}
                        pagination={pagination}
                        onChange={tableOnChange}
                        bordered
                        size="middle"
                        scroll={{
                          x: "max-content",
                        }}
                      />
                    </SkeletonTable>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </Content>
      <Drawer
        title={viewLaborRatesModal?.title + " Wages Details"}
        placement="right"
        width={1200}
        onClose={() => {
          setViewLaborRatesModal({
            status: false,
            title: "",
            rates: [],
          });
        }}
        open={viewLaborRatesModal?.status}
      >
        <div className="p-0">
          <Table
            columns={[
              {
                title: "Labor Flag",
                dataIndex: "laborFlag",
                key: "laborFlag",
                width: 200,
                // showSorterTooltip: { target: 'full-header' },
                // sorter: (a: any, b: any) => a.laborFlag.length - b.laborFlag.length,
                // sortDirections: ["ascend", "descend"],
                // defaultSortOrder: "ascend",
                // sortOrder: "ascend",

                render: (text: any, record: any) => {
                  return <span>{text}</span>;
                },
              },
              {
                title: "Type",
                dataIndex: "laborTypeName",
                key: "laborTypeName",
                width: 150,
              },
              {
                title: "Category",
                dataIndex: "laborCategoryName",
                key: "laborCategoryName",
                width: 150,
              },
              {
                title: "Payment Type",
                dataIndex: "paymentType",
                key: "paymentType",

                width: 120,
                render: (paymentType: any, record: any) => {
                  return <span>{paymentType}</span>;
                },
              },
              // Hourly Rate Fields
              {
                title: "Base Hourly Rate",
                dataIndex: "baseHourlyRate",
                key: "baseHourlyRate",
                width: 150,
                render: (amount: any, record: any) => {
                  if (record?.paymentType === "Hourly") {
                    return (
                      <span>
                        {formatCurrency(amount, record?.currency?.currencyCode)}
                        /hr
                      </span>
                    );
                  }
                  return <span>-</span>;
                },
              },
              {
                title: "Over Time Rate",
                dataIndex: "overTimeRate",
                key: "overTimeRate",
                width: 150,
                render: (amount: any, record: any) => {
                  if (record?.paymentType === "Hourly") {
                    return (
                      <span>
                        {formatCurrency(amount, record?.currency?.currencyCode)}
                        /hr
                      </span>
                    );
                  }
                  return <span>-</span>;
                },
              },
              {
                title: "Weekend Rate",
                dataIndex: "weekendRate",
                key: "weekendRate",
                width: 150,
                render: (amount: any, record: any) => {
                  if (record?.paymentType === "Hourly") {
                    return (
                      <span>
                        {formatCurrency(amount, record?.currency?.currencyCode)}
                        /hr
                      </span>
                    );
                  }
                  return <span>-</span>;
                },
              },
              {
                title: "Night Shift Rate",
                dataIndex: "nightShiftRate",
                key: "nightShiftRate",
                width: 150,
                render: (amount: any, record: any) => {
                  if (record?.paymentType === "Hourly") {
                    return (
                      <span>
                        {formatCurrency(amount, record?.currency?.currencyCode)}
                        /hr
                      </span>
                    );
                  }
                  return <span>-</span>;
                },
              },
              // Daily Rate Field
              {
                title: "Day Rate",
                dataIndex: "dayRate",
                key: "dayRate",
                width: 150,
                render: (amount: any, record: any) => {
                  if (record?.paymentType === "Daily") {
                    return (
                      <span>
                        {formatCurrency(amount, record?.currency?.currencyCode)}
                        /day
                      </span>
                    );
                  }
                  return <span>-</span>;
                },
              },
              // Monthly Rate Field
              {
                title: "Monthly Rate",
                dataIndex: "monthlyRate",
                key: "monthlyRate",
                width: 150,
                render: (amount: any, record: any) => {
                  if (record?.paymentType === "Monthly") {
                    return (
                      <span>
                        {formatCurrency(amount, record?.currency?.currencyCode)}
                        /month
                      </span>
                    );
                  }
                  return <span>-</span>;
                },
              },
            ]}
            dataSource={viewLaborRatesModal?.rates}
            bordered
            size="small"
          />
        </div>
      </Drawer>
    </>
  );
}

export default RateCardList;
